import React, { useCallback, useEffect, useState, useRef } from "react";
import useDarkMode from '../../hooks/useDarkMode';
import useThemeColor from "../../hooks/useThemeColor";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Preloader from "../../components/CustomComponents/preLoad/preLoader.js";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast.js";
import Select from "react-select";
import DropDownStyles, { DropDownStylesDark } from "./dropdownStyles.js";
import APIs from "../../Constants/apis.js";
import swal from "sweetalert";
import { SortOrderDropDownSelection } from "../../components/CustomComponents/DropdownSelections/DropDownSelections.jsx";
import { logout } from "../../app/auth/authSlice.js";
import { navigateToLogin } from "../../utils/authRedirect";
import { setVisibleColumns, setSortOrder, setSortBy } from "../../app/itemGridPreferencesSlice";
import { mergeVisibleColumns, needsPreferencesUpdate } from "../../utils/preferencesUtils";
import { MANAGE_PAGE_SEARCH_DEBOUNCE_MS } from "../../utils/managePageConstants";
import "./../order/ManageOrderFilters.css";

function ManageStaff() {
  const CompID = useSelector((state) => state.auth.user).userData?.compID;
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const [search, setSearch] = useState("");
  const [staffs, setStaffs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [roleLookup, setRoleLookup] = useState({});
  const [selectedItems, setSelectedItems] = useState(new Set());
  const lastSelectedIndex = useRef(null);

  // Check if dark mode is enabled
  const isDarkMode = useDarkMode();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;

  // Get theme color from centralized hook
  const themeColor = useThemeColor();

  

  const pageKey = "staff";
  const defaultVisibleColumns = {
    email: true,
    phone: true,
    role: true,
    status: true,
  };
  const defaultSortOrder = { value: "DESC", label: "Desc" };
  const defaultSortBy = { value: "First_Name", label: "Name" };
  const preferences = useSelector((state) => state.itemGridPreferences[pageKey] || {});
  const visibleColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
  const sortOrder = preferences.sortOrder || defaultSortOrder;
  const sortBy = preferences.sortBy || defaultSortBy;

  const PAGE_SIZE = 1000;
  const PAGE_NUMBER = 1;

  useEffect(() => {
    // Check if preferences exist and merge with defaults to ensure new columns are included
    if (!preferences.visibleColumns) {
      dispatch(setVisibleColumns({ pageKey, columns: defaultVisibleColumns }));
    } else {
      // Check if preferences need updating (missing new columns or have removed columns)
      if (needsPreferencesUpdate(preferences.visibleColumns, defaultVisibleColumns)) {
        // Merge existing preferences with default columns (this handles both additions and removals)
        const mergedColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
        dispatch(setVisibleColumns({ pageKey, columns: mergedColumns }));
      }
    }

    if (!preferences.sortOrder) {
      dispatch(setSortOrder({ pageKey, sortOrder: defaultSortOrder }));
    }
    if (!preferences.sortBy) {
      dispatch(setSortBy({ pageKey, sortBy: defaultSortBy }));
    }
    // eslint-disable-next-line
  }, [dispatch, pageKey]);

  const sortByOptions = [
    { value: "First_Name", label: "Name" },
    { value: "phoneNo", label: "Phone" },
  ];

  const clearFilters = () => {
    setSearch("");
  };

  const FetchStaffs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_COMPANY_STAFF, {
        params: {
          CompID,
          SortBy: sortBy.value,
          SortOrder: sortOrder.value,
          Search: search?.trim() || "",
          PageSize: PAGE_SIZE,
          PageNumber: PAGE_NUMBER,
        },
      });

      const staffData = response.data.data || response.data;

      if (Array.isArray(staffData)) {
        setStaffs(staffData);
      } else {
        // Unexpected API response format silently
        setStaffs([]);
      }

      setLoading(false);
    } catch (err) {
      setLoading(false);
      if (err.response && err.response.status === 401) {
        dispatch(logout());
        setTimeout(() => {
          navigateToLogin(navigate, location);
        }, 0);
      }
      showErrorToast("Failed to fetch staff.");
    }
  }, [
    search,
    sortBy,
    sortOrder,
    CompID,
    dispatch,
    navigate,
    location,
  ]);

  const fetchRoles = useCallback(async () => {
    if (!CompID) {
      return;
    }

    try {
      const response = await axiosInstance.get(APIs.GET_ROLES, {
        params: {
          CompID,
        },
      });

      const roleData = response.data.data || response.data;

      if (Array.isArray(roleData)) {
        const lookup = roleData.reduce((acc, role) => {
          acc[role.comRolePermission_ID] = role.r_Title;
          return acc;
        }, {});
        setRoleLookup(lookup);
      } else {
        setRoleLookup({});
      }
    } catch (err) {
      // Fail silently; roles will show as N/A
      setRoleLookup({});
    }
  }, [CompID]);

  const DeleteStaff = async (staff) => {
    const confirmDelete = await swal({
      title: "Are you sure?",
      text: `Are you sure you want to delete ${staff.userName}?`,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });

    if (!confirmDelete) {
      return;
    }

    setLoading(true);
    try {
      await axiosInstance
        .delete(APIs.DELETE_COMPANY_STAFF, {
          params: {
            CompID: staff.compID,
            UserName: staff.userName,
          },
        })
        .then((response) => {
          if (response.data.status) {
            setStaffs((prevStaffs) =>
              prevStaffs.filter(
                (delStaff) => delStaff.userName !== staff.userName
              )
            );
            showSuccessToast("Staff is Deleted.");
          } else {
            showErrorToast(response.data.message);
          }
        });
      setLoading(false);
    } catch (err) {
      showErrorToast("Something went wrong.");
      setLoading(false);
      // Error silently
    }
  };

  const handleSelectItem = (userName, index, event) => {
    setSelectedItems((prev) => {
      const newSelected = new Set(prev);

      if (event.shiftKey && lastSelectedIndex.current !== null) {
        const start = Math.min(lastSelectedIndex.current, index);
        const end = Math.max(lastSelectedIndex.current, index);
        for (let i = start; i <= end; i++) {
          if (staffs[i]?.userName) {
            newSelected.add(staffs[i].userName);
          }
        }
      } else {
        if (newSelected.has(userName)) {
          newSelected.delete(userName);
        } else {
          newSelected.add(userName);
        }
        lastSelectedIndex.current = index;
      }

      return newSelected;
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const newSelected = new Set(staffs.map((item) => item.userName));
      setSelectedItems(newSelected);
      lastSelectedIndex.current = null;
    } else {
      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
    }
  };

  const handleDeselectAll = () => {
    setSelectedItems(new Set());
    lastSelectedIndex.current = null;
  };

  const handleBulkDelete = async () => {
    if (selectedItems.size === 0) {
      showErrorToast("No staff selected for deletion.");
      return;
    }

    try {
      const confirmDelete = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to delete ${selectedItems.size} selected staff members?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmDelete) return;

      setLoading(true);
      await Promise.all(
        Array.from(selectedItems).map((userName) =>
          axiosInstance.delete(APIs.DELETE_COMPANY_STAFF, {
            params: {
              CompID,
              UserName: userName,
            },
          })
        )
      );

      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
      FetchStaffs();
      showSuccessToast(
        `${selectedItems.size} staff members deleted successfully.`
      );
      setLoading(false);
    } catch (err) {
      // Error in bulk delete silently
      showErrorToast("Failed to delete selected staff.");
      setLoading(false);
    }
  };

  const handleBulkEdit = () => {
    if (selectedItems.size === 0) {
      showErrorToast("No staff selected for editing.");
      return;
    }

    const selectedStaffUserNames = Array.from(selectedItems);
    navigate(`/team/bulkEdit`, { state: { selectedStaffUserNames } });
  };

  const handleBulkStatusChange = async (activate) => {
    if (selectedItems.size === 0) {
      showErrorToast(
        `No staff selected to ${activate ? "activate" : "deactivate"}.`
      );
      return;
    }

    try {
      const confirmAction = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to ${
          activate ? "activate" : "deactivate"
        } ${selectedItems.size} selected staff members?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmAction) return;

      setLoading(true);
      await Promise.all(
        Array.from(selectedItems).map((userName) =>
          axiosInstance.put(APIs.UPDATE_STAFF_STATUS, {
            CompID,
            UserName: userName,
            Active: activate,
          })
        )
      );

      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
      FetchStaffs();
      showSuccessToast(
        `${selectedItems.size} staff members ${
          activate ? "activated" : "deactivated"
        } successfully.`
      );
      setLoading(false);
    } catch (err) {
      // Error in bulk activate/deactivate silently
      showErrorToast(
        `Failed to ${activate ? "activate" : "deactivate"} selected staff.`
      );
      setLoading(false);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      FetchStaffs();
    }, MANAGE_PAGE_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handler);
  }, [FetchStaffs]);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  return (
    <div>
      {loading && <Preloader />}
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item active">Staff Management</li>
          </ol>
          <h1 className="page-header mb-0">Manage Your Staff Efficiently
          </h1>
        </div>
        <div className="ms-auto">
          <Link to="/team/addTeamMember">
            <button 
              className="btn px-4"
              style={{ 
                backgroundColor: themeColor,
                background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                border: 'none',
                color: 'white',
                borderRadius: '8px',
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'all 0.3s ease',
                boxShadow: `0 2px 8px ${themeColor}40`
              }}
              onMouseEnter={(e) => {
                e.target.style.background = `linear-gradient(135deg, ${themeColor}dd 0%, ${themeColor} 100%)`;
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
              }}
              onMouseLeave={(e) => {
                e.target.style.background = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
              }}
            >
              <i className="fa fa-plus fa-lg me-2 ms-n2 text-white"></i>
              Add New Staff
            </button>
          </Link>
        </div>
      </div>

      <div className="mb-3 mt-2">
        <div className="card border-0">
          <div className="tab-content p-3">
            <div className="tab-pane fade show active" id="allTab">
              {/* Enhanced Filter Section */}
                <div className="mb-4">
                  <div
                    className="card filter-card"
                    style={{
                      position: "relative",
                      zIndex: 1,
                      background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                      borderRadius: '12px',
                      border: 'none',
                      boxShadow: `0 4px 20px ${themeColor}40`
                    }}
                  >
                    <div className="card-body p-3">
                      {/* Filter Controls */}
                      <div className="row g-3 align-items-center">
                        {/* Search Field */}
                        <div className="col-xl-3 col-lg-3 col-md-6">
                          <div className="search-input filter-control">
                            <input
                              type="text"
                              className="filter-input"
                              value={search}
                              onChange={(e) => setSearch(e.target.value)}
                              placeholder="Search by name,  phone..."
                              style={{
                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                border: '1px solid rgba(255, 255, 255, 0.3)',
                                color: '#333'
                              }}
                            />
                          </div>
                        </div>

                        {/* Sort By Dropdown */}
                        <div className="col-xl-2 col-lg-2 col-md-6">
                          <div className="filter-control">
                            <Select
                              value={sortBy}
                              onChange={(val) => dispatch(setSortBy({ pageKey, sortBy: val }))}
                              options={sortByOptions}
                              styles={dropdownStyles}
                              placeholder="Sort By"
                              className="filter-dropdown"
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              menuShouldBlockScroll={false}
                              menuShouldScrollIntoView={false}
                              closeMenuOnScroll={false}
                            />
                          </div>
                        </div>

                        {/* Sort Order Dropdown */}
                        <div className="col-xl-2 col-lg-2 col-md-6">
                          <div className="filter-control">
                            <SortOrderDropDownSelection
                              defaultValue={sortOrder}
                              onChange={(val) => dispatch(setSortOrder({ pageKey, sortOrder: val }))}
                            />
                          </div>
                        </div>
                        <div className="col-xl-3 col-lg-3 col-md-12">
                          <div className="d-flex gap-2">
                            <button
                              className="clear-btn"
                              onClick={clearFilters}
                              title="Clear all filters"
                            >
                              <i className="fa fa-times me-1"></i>
                              Filter
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Selection Info and Actions (Shown only when staff are selected) */}
                {selectedItems.size > 0 && (
                  <div className="mb-3">
                    <div className="p-3 bg-light rounded d-flex align-items-center justify-content-between" style={{ border: "1px solid #dee2e6" }}>
                      <div className="d-flex align-items-center gap-3">
                        <span className="fw-bold text-primary">
                          {selectedItems.size} staff member{selectedItems.size > 1 ? 's' : ''} selected
                        </span>
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={handleDeselectAll}
                        >
                          Clear Selection
                        </button>
                      </div>

                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={handleBulkEdit}
                          title="Edit Selected Staff"
                        >
                          <i className="fa fa-edit me-1"></i>
                          Edit
                        </button>
                        <button
                          className="btn btn-sm btn-success"
                          onClick={() => handleBulkStatusChange(true)}
                          title="Activate Selected Staff"
                        >
                          <i className="fa fa-check-circle me-1"></i>
                          Activate
                        </button>
                        <button
                          className="btn btn-sm btn-warning"
                          onClick={() => handleBulkStatusChange(false)}
                          title="Deactivate Selected Staff"
                        >
                          <i className="fa fa-times-circle me-1"></i>
                          Deactivate
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={handleBulkDelete}
                          title="Delete Selected Staff"
                        >
                          <i className="fa fa-trash me-1"></i>
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <div
                  className="table-responsive mt-3"
                  style={{
                    minHeight: "200px",
                    maxHeight: "450px",
                    overflowY: "auto",
                    overflowX: "auto",
                  }}
                >
                  <table className="table table-hover">
                    <thead
                      className="bg-light"
                      style={{
                        position: "sticky",
                        top: 0,
                        zIndex: 5,
                        backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                        color: isDarkMode ? "#ffffff" : "#000000",
                      }}
                    >
                      <tr>
                        <th className="pt-2 pb-2">
                          <div className="form-check">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              onChange={handleSelectAll}
                              checked={
                                staffs.length > 0 &&
                                selectedItems.size === staffs.length
                              }
                            />
                            <label className="form-check-label"></label>
                          </div>
                        </th>
                        <th className="pt-0 pb-2">Name</th>
                        {visibleColumns.email && <th className="pt-0 pb-2">Email</th>}
                        {visibleColumns.phone && <th className="pt-0 pb-2">Phone Number</th>}
                        {visibleColumns.role && <th className="pt-0 pb-2">Role</th>}
                        {visibleColumns.status && <th className="pt-0 pb-2">Status</th>}
                        <th className="pt-0 pb-2 text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="7" className="text-center py-4">
                            <div className="spinner-border spinner-border-sm text-primary" role="status">
                              <span className="visually-hidden">Loading...</span>
                            </div>
                            <span className="ms-2">Loading...</span>
                          </td>
                        </tr>
                      ) : staffs.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="text-center py-4">
                            <div className="text-muted">
                              <i className="fa fa-search fa-2x mb-2"></i>
                              <p className="mb-0">
                                {search.trim() ? "No staff members found matching your search criteria." : "No staff members available."}
                              </p>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        staffs.map((staff, index) => (
                        <tr key={staff.userName}>
                          <td className="w-10px align-middle">
                            <div className="form-check">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                checked={selectedItems.has(staff.userName)}
                                onChange={(e) =>
                                  handleSelectItem(staff.userName, index, e)
                                }
                                data-chkbox-shiftsel="type1"
                              />
                              <label className="form-check-label"></label>
                            </div>
                          </td>
                          <td
                            className="align-middle"
                            onClick={() => {
                              navigate(`/team/updateTeamMember/${staff.userName}`);
                            }}
                            style={{ cursor: "pointer" }}
                          >
                            {staff.firstName} {staff.lastName}
                          </td>
                          {visibleColumns.email && (
                            <td
                              className="align-middle"
                              onClick={() => {
                                navigate(`/team/updateTeamMember/${staff.userName}`);
                              }}
                              style={{ cursor: "pointer" }}
                            >
                              {staff.email}
                            </td>
                          )}
                          {visibleColumns.phone && (
                            <td
                              className="align-middle"
                              onClick={() => {
                                navigate(`/team/updateTeamMember/${staff.userName}`);
                              }}
                              style={{ cursor: "pointer" }}
                            >
                              {staff.phoneNo}
                            </td>
                          )}
                          {visibleColumns.role && (
                            <td
                              className="align-middle"
                              onClick={() => {
                                navigate(`/team/updateTeamMember/${staff.userName}`);
                              }}
                              style={{ cursor: "pointer" }}
                            >
                              {roleLookup[staff.comRolePermission_ID] || "N/A"}
                            </td>
                          )}
                          {visibleColumns.status && (
                            <td className="align-middle">
                              {staff.active ? (
                                <span className="badge border border-success text-success px-2 pt-5px pb-5px rounded fs-12px d-inline-flex align-items-center">
                                  <i className="fa fa-circle fs-9px fa-fw me-5px"></i> Active
                                </span>
                              ) : (
                                <span className="badge border border-warning text-warning px-2 pt-5px pb-5px rounded fs-12px d-inline-flex align-items-center">
                                  <i className="fa fa-circle fs-9px fa-fw me-5px"></i> Pending
                                </span>
                              )}
                            </td>
                          )}
                          <td className="align-middle text-center">
                            <div className="d-flex justify-content-center gap-2">
                              {/* Edit Icon */}
                              <Link
                                to={`/team/updateTeamMember/${staff.userName}`}
                                className="btn btn-sm btn-outline-primary"
                                title="Edit Staff"
                                style={{
                                  width: '32px',
                                  height: '32px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  borderRadius: '6px',
                                  border: `1px solid ${themeColor}`,
                                  color: themeColor,
                                  transition: 'all 0.2s ease'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = themeColor;
                                  e.currentTarget.style.color = 'white';
                                  e.currentTarget.style.transform = 'scale(1.05)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = 'transparent';
                                  e.currentTarget.style.color = themeColor;
                                  e.currentTarget.style.transform = 'scale(1)';
                                }}
                              >
                                <i className="fa fa-edit" style={{ fontSize: '12px' }}></i>
                              </Link>

                              {/* Delete Icon */}
                              <button
                                onClick={() => DeleteStaff(staff)}
                                className="btn btn-sm btn-outline-danger"
                                title="Delete Staff"
                                style={{
                                  width: '32px',
                                  height: '32px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  borderRadius: '6px',
                                  border: '1px solid #dc3545',
                                  color: '#dc3545',
                                  transition: 'all 0.2s ease'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#dc3545';
                                  e.currentTarget.style.color = 'white';
                                  e.currentTarget.style.transform = 'scale(1.05)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = 'transparent';
                                  e.currentTarget.style.color = '#dc3545';
                                  e.currentTarget.style.transform = 'scale(1)';
                                }}
                              >
                                <i className="fa fa-trash" style={{ fontSize: '12px' }}></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
  );
}

export default ManageStaff;